import React, {useEffect, useState} from 'react';
import {get} from 'lodash';
import propTypes from 'prop-types';
import styled from '@emotion/styled';
import parse from 'html-react-parser';
import { Select, MenuItem, InputLabel, FormControl, ButtonBase } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {theme} from '../../../themes/default-theme';
import {ReactSVG} from 'react-svg';
import chevronDown from '../../../assets/icons/new-chevron-down.svg';
import mobileCheck from '../../../util/mobileCheck';
import Router from 'next/router';

export const dropdownTheme = createTheme({
  ...theme,
  components: {
    ...theme.components,
    Complete: {
      styleOverrides: {
        root: {
          'fieldset': {
            background: 'red'
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-outlined": {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            paddingLeft: 19,
            fontSize: 14,
            '&.MuiInputLabel-shrink': {
              transform: 'translateY(-50%)'
            },
            [theme.breakpoints.up('md')]: {
              fontSize: 16
            }
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: theme.palette.darkGrey,
          },
          '&.MuiFormLabel-filled': {
            display: 'none'
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          "&.MuiList-padding": {
            padding: 0,
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          "&.MuiPaper-elevation8": {
            boxShadow: 'none',
            marginTop: 20,
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.middleGrey}`,
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          minHeight: '20px',
          lineHeight: '20px',
          "&.MuiSelect-select:focus": {
            backgroundColor: 'transparent'
          }
        },
        select: {
          '&.Mui-disabled': {
            opacity: 0.5
          }
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: theme.palette.vividBlue,
            color: 'white'
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          padding: '12.5px 19px',
          paddingTop: '12.5px',
          paddingBottom: '12.5px',
          "&:not(:last-child)": {
            borderBottom: `1px solid ${theme.palette.middleGrey}`,
          },
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: theme.palette.vividBlue,
          color: 'white'
        }
        }
      }
    }
  }
})

const liteTheme = createTheme({
  ...theme,
  components: {
    ...dropdownTheme.components,
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          border: 0,
          color: theme.palette.white,
        },
        notchedOutline: {
          border: 0
        },
        input: {
          padding: '5px 14px'
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.white,
        },
        filled: {
          display: 'none !important',
          opacity: 0
        }
      }
    }
  }
})

const ChevronIcon = styled(ButtonBase)`
  pointer-events: none;
  width: 7px;
  height: 14px;
  position: absolute;
  right: 17px;
  transition: transform 300ms ease-in-out;

  ${props => props.theme === 'lite' ? `
    top: 0;
  ` : `

  `}

  ${theme.breakpoints.up('md')} {
    ${props => props.theme === 'lite' && `
      top: 0;
    `}
  }

  ${props => props.active === 1 && `
    transform: rotate(180deg);
  `}


  }
`

const newDropdownDefaultPropOnChange = () => {};
const newDropdownDefaultPropOnUpdate = () => {};

const NewDropdown = ({
  label = 'Select',
  options,
  onChange = newDropdownDefaultPropOnChange,
  onUpdate = newDropdownDefaultPropOnUpdate,
  alwaysNative = false,
  neverNative = false,
  defaultValue = undefined,
  value = undefined,
  formVal = undefined,
  error = false,
  showComplete = false,
  disabled = false,
  link = false,
  theme = 'regular',
  open = false,
  scrollOnOpen = false
}) => {

  const [isNative, setNative] = useState(true);
  const [isOpen, setOpen] = useState(open);

  useEffect(() => {
    setOpen(open);
  }, [open])

  const handleChange = (value) => {
    if (link) {
      Router.push(value);
      return;
    }
    const item = options.find(item => item.value.toString() === value.toString());
    onChange(item)
    onUpdate(item) // for react hook forms
  }

  useEffect(() => {
    if (neverNative) {
      setNative(false);
    } else {
      const native = alwaysNative ? true : mobileCheck();
      setNative(native)
    }
  },[])


  const disableScroll = (e) => {
    if (document.body.classList.contains('overflow-scroll-bar')) {
      e.preventDefault();
    }
  }

  useEffect(() => {
    if (!scrollOnOpen) {
      if (isOpen) {
        document.body.classList.add('overflow-scroll-bar')
      } else {
        document.body.classList.remove('overflow-scroll-bar')
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (!scrollOnOpen) {
      window.addEventListener("wheel",  (e) => disableScroll(e), {passive: false});
      return window.removeEventListener("wheel", disableScroll);
    }
  }, [])

  const val = typeof(value) === 'object' ? get(value, 'value', undefined) : value
  
  return (
    <ThemeProvider theme={theme === 'lite' ? liteTheme : dropdownTheme}>
      <FormControl variant="outlined" fullWidth classes={{ root: showComplete && !error ? dropdownTheme.Complete : ''}}>
        {!isNative && !formVal &&
        <InputLabel 
          disableAnimation={true}
          focused={false}
          shrink={false}
          disabled={disabled}
          >{label}</InputLabel>
        }
        <Select
          onChange={(e) => {handleChange(e.target.value)}}
          native={isNative}
          value={formVal ? formVal.value : val || ''}
          defaultValue={defaultValue || undefined}
          error={error}
          disabled={disabled}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          IconComponent={() => (
            <ChevronIcon theme={theme}>
              <ReactSVG src={chevronDown} />
            </ChevronIcon>
          )}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center"
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "center"
            },
            getContentAnchorEl: null
          }}
        >
          {isNative && !defaultValue && label && <option value="">{label}</option> }
          {options.map(item => (
            isNative ? 
              <option key={item.id || item.value} value={item.value}>{parse(item.text)}</option>
              :
              <MenuItem key={item.id || item.value} value={item.value}>{parse(item.text)}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </ThemeProvider>
  )
}

NewDropdown.propTypes = {
  label: propTypes.string,
  options: propTypes.array.isRequired,
  onChange: propTypes.func,
  onUpdate: propTypes.func,
  alwaysNative: propTypes.bool,
  formVal: propTypes.object,
  defaultValue: propTypes.string,
  value: propTypes.any,
  error: propTypes.bool,
  showComplete: propTypes.bool,
  disabled: propTypes.bool,
  link:  propTypes.bool,
  neverNative: propTypes.bool,
  theme: propTypes.oneOf(['regular', 'lite']),
  open: propTypes.bool,
  scrollOnOpen: propTypes.bool
}

export default NewDropdown
